import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ShareAltOutlined, CalendarOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import SingleManifestoTable from "@/pages/manifesto/components/single_manifesto_details/SingleManifestoTable";
import { selectCurrentManifesto } from "@/store/redux/slices/manifesto/manifesto_slice";
import {
  fetchEventByAny,
  selectEventsByManifesto,
} from "@/store/redux/slices/event/event_slice";

const CampaignInfo = ({ date, title, description }) => {
  const [, setSelectedCampaign] = useState([]);
  const currentManifesto = useSelector(selectCurrentManifesto);
  const events = useSelector(selectEventsByManifesto);
  const dispatch = useDispatch();

  // Check if events actually has content
  const hasEvents =
    Array.isArray(events) &&
    events.length > 0 &&
    events.some((arr) => arr.length > 0);

  useEffect(() => {
    const fetchEvents = async () => {
      if (currentManifesto?._id) {
        await dispatch(
          fetchEventByAny({
            key: "manifestoID",
            value: currentManifesto._id,
          })
        );
      }
    };
    fetchEvents();
  }, [dispatch, currentManifesto?._id]);

  return (
    <div className="text-left">
      <div className="flex justify-between">
        <div className="flex flex-col items-start gap-1 mb-4">
          <h3 className="font-bold tracking-wide text-primary text-small">
            {date}
          </h3>
        </div>

        <div className="flex items-center gap-4 p-2 bg-secondary">
          <span className="font-normal uppercase text-lighterGrey text-small">
            SHARE
          </span>
          <ShareAltOutlined style={{ fontSize: "15px" }} />
          <WebIcon icon="Message" size="small" />
        </div>
      </div>

      <h1 className="mb-4 whitespace-pre-line heading-regular">{title}</h1>

      <p className="mb-8 font-normal leading-relaxed text-bold">
        {description}
      </p>

      {/* Show empty state message if no events */}
      {!hasEvents && (
        <div className="py-10 text-center">
          <p className="text-gray-500">
            No campaign events found for this manifesto
          </p>
        </div>
      )}

      {/* Desktop View - Table - only show if has events */}
      {hasEvents && (
        <div className="hidden lg:block">
          <SingleManifestoTable
            setSelectedCampaign={setSelectedCampaign}
            events={events[0]}
          />
        </div>
      )}

      {/* Mobile View - Cards - only show if has events */}
      {hasEvents && (
        <div className="lg:hidden space-y-4">
          {events[0]?.map((event) => (
            <Link
              key={event._id}
              to={`/dashboard/events/${event.id || event._id}`}
              className="block">
              <div className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="text-small text-primary">
                      #{event.id || event._id}
                    </span>
                    <h4 className="mt-1 font-semibold text-darkGrey">
                      {event.name}
                    </h4>
                    <div className="flex items-center mt-2 text-lighterGrey text-small">
                      <CalendarOutlined className="mr-1" />
                      <span>
                        Published on{" "}
                        {new Date(event.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                        })}
                      </span>
                    </div>
                  </div>
                  <button
                    className="p-2"
                    onClick={(e) => {
                      e.preventDefault();
                    }}>
                    <CustomMoreOptions
                      variant="filled"
                      orientation="vertical"
                    />
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

CampaignInfo.propTypes = {
  date: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default CampaignInfo;
